// WorkflowBuilder — the create-a-workflow view rendered inside FormWorkflowModal
// (list → builder → back). A named workflow of ordered steps; each step has a
// role and a list of response→routing rows. Save draft / Publish share one
// payload (POST /workflows/{draft,publish}); server-owned ids are sent null on
// creation and only linked via client `stepReferenceId`s.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import { Button, Icon, IconButton, Input, type SelectDropdownOption } from '@/components/common';
import { useGetRolesQuery } from '@/api/endpoints/rolesApi';
import { useGetWorkflowResponseTagsQuery } from '@/api/endpoints/tagsApi';
import {
  WorkflowElementActionType,
  useGetWorkflowDetailQuery,
  useSaveWorkflowDraftMutation,
  usePublishWorkflowMutation,
  type WorkflowDetail,
  type WorkflowSavePayload,
} from '@/api/endpoints/workflowsApi';
import WorkflowStepCard from './WorkflowStepCard';
import WorkflowVersionsModal from './WorkflowVersionsModal';

/** A response→routing row in the local builder model. */
export interface BuilderResponse {
  id: string | null;
  elementGlobalId: string | null;
  /** Chosen response tag title (sent as element.title). */
  responseTitle: string;
  action: WorkflowElementActionType;
  nextStepId: string | null;
  /** The target step's `stepRefId` — only a `next_step` route has one; the two
   *  terminal actions (end_workflow / back_to_user) carry null. */
  nextStepRefId: string | null;
  nextStepReferenceId: string | null;
}

export interface BuilderStep {
  id: string | null;
  stepGlobalId: string | null;
  stepRefId: string;
  stepReferenceId: string | null;
  title: string;
  roleId: string;
  responses: BuilderResponse[];
}

interface WorkflowBuilderProps {
  /** Existing workflow id → edit mode (seeds from detail); undefined → create. */
  workflowId?: string;
  /** Return to the list view. */
  onExit: () => void;
}

// Seed the local builder model from a fetched workflow detail. A saved step's
// local identity (stepRefId) IS its stepGlobalId, which the detail always
// carries — and an element's `nextStep` is that same stepGlobalId, so both
// sides of a route already speak one id space and nothing has to be translated.
const seedFromDetail = (detail: WorkflowDetail): BuilderStep[] =>
  detail.steps.map((s) => ({
    id: s.id,
    stepGlobalId: s.stepGlobalId,
    stepRefId: s.stepGlobalId,
    stepReferenceId: null,
    title: s.title,
    roleId: s.roleId,
    responses: s.elements.map((e) => ({
      id: e.id,
      elementGlobalId: e.elementGlobalId,
      responseTitle: e.title,
      action: e.actionType,
      nextStepId: e.nextStepId,
      nextStepRefId: e.nextStepId,
      nextStepReferenceId: null,
    })),
  }));

const newStep = (title: string): BuilderStep => {
  const stepRefId = uuidv4();
  return {
    id: null,
    stepGlobalId: null,
    stepRefId,
    stepReferenceId: stepRefId,
    title,
    roleId: '',
    responses: [],
  };
};

const WorkflowBuilder = ({ workflowId, onExit }: WorkflowBuilderProps) => {
  const { t } = useTranslation();
  const stepTitle = (n: number) => t('forms_workflow_step_title', { n });
  const [title, setTitle] = useState('');
  const [steps, setSteps] = useState<BuilderStep[]>(() => [
    newStep(t('forms_workflow_step_title', { n: 1 })),
  ]);
  const [submitted, setSubmitted] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);

  const { data: roles = [] } = useGetRolesQuery();
  const { data: responseTags = [] } = useGetWorkflowResponseTagsQuery();
  const {
    data: detail,
    isLoading: loadingDetail,
    isError: detailError,
  } = useGetWorkflowDetailQuery(workflowId ?? '', { skip: !workflowId });
  const [saveWorkflowDraft, { isLoading: savingDraft }] = useSaveWorkflowDraftMutation();
  const [publishWorkflow, { isLoading: publishing }] = usePublishWorkflowMutation();

  // Edit mode — seed the name + steps once the detail arrives.
  useEffect(() => {
    if (detail) {
      setTitle(detail.title);
      setSteps(seedFromDetail(detail));
    }
  }, [detail]);

  const roleOptions: SelectDropdownOption[] = useMemo(
    () => roles.filter((r) => !r.isOwner).map((r) => ({ value: r.id, label: r.name })),
    [roles],
  );
  const responseOptions: SelectDropdownOption[] = useMemo(
    () => responseTags.map((tag) => ({ value: tag.title, label: tag.title })),
    [responseTags],
  );

  const titleMissing = !title.trim();

  // Edit mode cannot save until the real steps have arrived: until then the
  // canvas still holds the create-mode defaults (one blank step), and saving
  // those would overwrite the workflow with an empty one — buildPayload keeps
  // sending the real id throughout. Keyed on `detail` rather than the loading
  // flag so a FAILED fetch stays blocked too, not just a slow one.
  const detailPending = Boolean(workflowId) && !detail;

  const updateStep = (index: number, next: BuilderStep) =>
    setSteps((prev) => prev.map((s, i) => (i === index ? next : s)));
  const addStep = () => setSteps((prev) => [...prev, newStep(stepTitle(prev.length + 1))]);
  const deleteStep = (index: number) =>
    setSteps((prev) => {
      const removed = prev[index];
      return prev
        .filter((_, i) => i !== index)
        .map((s) => ({
          ...s,
          // Drop any routes that pointed at the removed step.
          responses: s.responses.map((r) =>
            r.action === WorkflowElementActionType.NextStep && r.nextStepRefId === removed.stepRefId
              ? {
                  ...r,
                  action: WorkflowElementActionType.EndWorkflow,
                  nextStepRefId: null,
                  nextStepId: null,
                }
              : r,
          ),
        }));
    });

  const buildPayload = (): WorkflowSavePayload => ({
    workflowId: workflowId ?? null,
    title: title.trim(),
    steps: steps.map((s, i) => ({
      _id: s.id ?? null,
      stepGlobalId: s.stepGlobalId ?? null,
      title: s.title,
      position: i + 1,
      stepReferenceId: s.stepReferenceId ?? null,
      role: s.roleId,
      elements: s.responses.map((r, j) => {
        const target =
          r.action === WorkflowElementActionType.NextStep
            ? steps.find((x) => x.stepRefId === r.nextStepRefId)
            : undefined;
        return {
          _id: r.id ?? null,
          elementGlobalId: r.elementGlobalId ?? null,
          title: r.responseTitle,
          position: j + 1,
          actionType: r.action,
          nextStepId: target?.stepGlobalId ?? null,
          nextStepReferenceId: target?.stepReferenceId ?? null,
        };
      }),
    })),
  });

  const saveDraft = async () => {
    setSubmitted(true);
    if (titleMissing || detailPending) return;
    try {
      await saveWorkflowDraft(buildPayload()).unwrap();
      toast.success(t('forms_workflow_draft_saved'));
      onExit();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Both writers re-check `detailPending` rather than trusting the disabled
  // buttons alone — overwriting a real workflow with the blank defaults is the
  // one failure here that cannot be undone.
  const publish = async () => {
    setSubmitted(true);
    if (titleMissing || detailPending) return;
    try {
      await publishWorkflow(buildPayload()).unwrap();
      toast.success(t('forms_workflow_published'));
      onExit();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  return (
    <div className='workflow-builder'>
      <div className='workflow-builder__bar'>
        <div className='workflow-builder__lead'>
          <IconButton
            icon='arrow-left'
            variant='outline'
            aria-label={t('forms_workflow_back')}
            onClick={onExit}
          />
          <div className='workflow-builder__heading'>
            <Input
              className='workflow-builder__name'
              placeholder={t('forms_workflow_name_ph')}
              value={title}
              error={submitted && titleMissing}
              onChange={(e) => setTitle(e.target.value)}
            />
            <span className='workflow-builder__subtitle'>
              {t('forms_workflow_steps', { count: steps.length })}
              {detail?.version != null && ` · v${detail.version}`}
            </span>
          </div>
        </div>
        <div className='workflow-builder__actions'>
          {workflowId && (
            <Button variant='secondary' iconStart='history' onClick={() => setVersionsOpen(true)}>
              {t('forms_workflow_version_history')}
            </Button>
          )}
          <Button variant='secondary' onClick={onExit}>
            {t('forms_workflow_discard')}
          </Button>
          {/* The two writers lock each other: `loading` keeps the spinner on
              whichever was clicked, `disabled` stops the other — otherwise a
              second click during an in-flight draft creates a SECOND workflow
              (create mode sends workflowId: null both times). Discard and the
              back arrow stay enabled: they don't write, and trapping the user
              in a failed-fetch state would be worse. */}
          <Button
            variant='secondary'
            iconStart='save'
            loading={savingDraft}
            disabled={publishing || detailPending}
            onClick={saveDraft}
          >
            {t('forms_workflow_save_draft')}
          </Button>
          <Button
            iconStart='circle-check'
            loading={publishing}
            disabled={savingDraft || detailPending}
            onClick={publish}
          >
            {t('forms_workflow_publish')}
          </Button>
        </div>
      </div>

      {/* Edit mode shows its state instead of the canvas — rendering the
          create-mode defaults here is what made a failed fetch look like a new
          workflow. No retry button: the back arrow above is still live and
          keepUnusedDataFor:0 means reopening the row refetches from scratch. */}
      {loadingDetail ? (
        <p className='workflow-builder__state'>{t('text_loading')}</p>
      ) : detailError ? (
        <p className='workflow-builder__state'>
          <Icon name='circle-alert' size={26} />
          {t('error_generic')}
        </p>
      ) : (
        <div className='workflow-builder__canvas'>
          <div className='workflow-builder__node'>
            <span className='workflow-builder__node-icon'>
              <Icon name='user' size={20} />
            </span>
            <span className='workflow-builder__node-label'>{t('forms_workflow_filler')}</span>
          </div>
          <Icon
            name='arrow-right'
            size={18}
            className='workflow-builder__arrow'
            aria-hidden='true'
          />

          {steps.map((step, i) => (
            <div key={step.stepRefId} className='workflow-builder__flow-item'>
              <WorkflowStepCard
                step={step}
                index={i}
                steps={steps}
                roleOptions={roleOptions}
                responseOptions={responseOptions}
                canDelete={steps.length > 1}
                onChange={(next) => updateStep(i, next)}
                onDelete={() => deleteStep(i)}
              />
              <Icon
                name='arrow-right'
                size={18}
                className='workflow-builder__arrow'
                aria-hidden='true'
              />
            </div>
          ))}

          <button type='button' className='workflow-builder__add-step' onClick={addStep}>
            <Icon name='plus' size={22} />
            {t('forms_workflow_add_step')}
          </button>
          <Icon
            name='arrow-right'
            size={18}
            className='workflow-builder__arrow'
            aria-hidden='true'
          />

          <div className='workflow-builder__node'>
            <span className='workflow-builder__node-icon'>
              <Icon name='flag' size={20} />
            </span>
            <span className='workflow-builder__node-label'>{t('forms_workflow_end')}</span>
          </div>
        </div>
      )}

      {/* Only edit mode has versions to show — and the trigger above is gated
          the same way. `detail.version` is the workflow's current version; the
          versions response cannot identify it on its own. */}
      {workflowId && (
        <WorkflowVersionsModal
          open={versionsOpen}
          workflowId={workflowId}
          currentVersion={detail?.version ?? null}
          onClose={() => setVersionsOpen(false)}
        />
      )}
    </div>
  );
};

export default WorkflowBuilder;
